import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { Path, PathId } from "features/Path/models";
import { apiDuplicatePrebuiltPathPathById, apiPrebuiltPathPathById } from "helpers/url/api/v2";
import { generatePath, useNavigate } from "react-router";
import apiClient from "services/ApiClient";
import { pathKeys } from "features/Path/hooks";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import { PrebuiltPath } from "./models";

export const prebuiltPathKeys = {
  all: ["prebuiltPaths"] as const,
  lists: () => [...prebuiltPathKeys.all, "list"] as const,
  list: (filters: string) => [...prebuiltPathKeys.lists(), { filters }] as const,
  details: () => [...prebuiltPathKeys.all, "detail"] as const,
  detail: (id: string) => [...prebuiltPathKeys.details(), id] as const,
};

export const usePrebuiltPathDetailQuery = (id: string | undefined, options = {}) => useQuery<PrebuiltPath>({
  queryKey: prebuiltPathKeys.detail(id),
  queryFn: async () => apiClient.get(apiPrebuiltPathPathById(id)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
});

export const usePrebuiltPathListQuery = (options = {}) => useQuery<PrebuiltPath[]>({
  queryKey: prebuiltPathKeys.lists(),
  queryFn: async () => apiClient.get(generatePath(API_ROUTES.prebuilt.paths.base)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export function useDuplicatePrebuiltPath({ prebuiltPathId, onClose }: { prebuiltPathId: PathId | undefined, onClose?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  return useMutation(
    {
      mutationFn: async (data: { name: string; autoAdd: boolean }) => apiClient.post<PrebuiltPath>(
        apiDuplicatePrebuiltPathPathById(prebuiltPathId as string),
        data,
      ),
      onSuccess: (res: Path) => {
        console.log(res);
        queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
        onClose?.();
        toast({
          title: "Path duplicated",
          status: "success",
        });
        navigate(v2pathPathById(res?.id));
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to duplicate the path. Please try again.", status: "error" });
      },
    },
  );
}
